/**
 * Этап 4 / KS-06 ч.2 — inline images in rich text, and their conversion to email
 * `cid:` attachments.
 *
 * ── Why images travel as `data:` in storage.
 * The recruiter signature has to carry a logo (the client called signatures "прям
 * очень критичны"). Three carriers were possible:
 *
 *   - an R2 object URL — but `R2_PUBLIC_BASE_URL` is not configured, so R2 hands back
 *     a PRESIGNED url that expires after 7 days (S3 SigV4 hard cap). A signature
 *     whose logo breaks a week later is worse than no logo;
 *   - a URL served by our own api — needs an endpoint that authenticates an `<img>`
 *     tag, i.e. cookie auth, which this app does not use for the API;
 *   - a `data:` URI inside the signature HTML — renders natively in the editor and
 *     the preview, needs no endpoint, no bucket and no expiry.
 *
 * The third wins for STORAGE. It is safe here specifically because it never reaches
 * the recipient as `data:`: {@link inlineImagesAsCid} converts every inline image
 * into a MIME attachment with a Content-ID before the message is sent. Gmail and
 * Outlook strip `data:` images from a body — which is exactly why the CID step is
 * mandatory rather than an optimisation.
 *
 * The allowance is narrow on purpose: `data:` is permitted ONLY on `<img src>`, ONLY
 * for a known raster image type. `data:text/html` — the payload that makes `data:`
 * dangerous — stays banned everywhere, including here.
 */
/** Image media types allowed in a `data:` URI. Raster only — no SVG. */
export const INLINE_IMAGE_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
];
/**
 * SVG is deliberately absent from {@link INLINE_IMAGE_MIME_TYPES}: an SVG is a
 * document, not a bitmap — it can carry `<script>` and external references, so an
 * `<img src="data:image/svg+xml,…">` is an injection vector wearing an image's name.
 * Mail clients render it poorly anyway.
 */
export const INLINE_IMAGE_DATA_URI_RE = new RegExp(`^data:(${INLINE_IMAGE_MIME_TYPES.join('|').replace(/\//g, '\\/')});base64,([a-z0-9+/=\\s]+)$`, 'i');
/**
 * Largest inline image accepted, measured on the BASE64 payload — i.e. on what is
 * actually stored in `users.email_signature` and copied into EVERY outgoing message.
 *
 * 256 KB base64 ≈ 190 KB of image, which is generous for a logo and small enough that
 * a signature never dominates the message size. The limit exists because the natural
 * mistake is to pick a phone photo: 4 MB of base64 would bloat the row, push the
 * message toward the 4 MB Graph request ceiling (`ATTACHMENT_TOTAL_LIMIT_BYTES`) and
 * be re-sent with every email.
 */
export const INLINE_IMAGE_MAX_BASE64_BYTES = 256 * 1024;
/** True when a `data:` image URI is within {@link INLINE_IMAGE_MAX_BASE64_BYTES}. */
export function isInlineImageWithinLimit(dataUri) {
    const m = INLINE_IMAGE_DATA_URI_RE.exec(String(dataUri ?? '').trim());
    if (!m)
        return false;
    return m[2].replace(/\s+/g, '').length <= INLINE_IMAGE_MAX_BASE64_BYTES;
}
/** True when the value is a `data:` URI for an allowed raster image type. */
export function isInlineImageDataUri(value) {
    return INLINE_IMAGE_DATA_URI_RE.test(String(value ?? '').trim());
}
/** File extension for an allowed image mime type (used to name the attachment). */
function extensionFor(mimeType) {
    const subtype = mimeType.split('/')[1] ?? 'png';
    return subtype === 'jpeg' ? 'jpg' : subtype;
}
/**
 * Rewrite every `data:`-image in the HTML to a `cid:` reference and return the
 * extracted images alongside.
 *
 * `cid` values are derived from the image's POSITION (`inline-image-1`, `-2`, …),
 * not from a random id or a hash: the function must be deterministic so the same
 * message produces the same bytes on a retry, and `@eer/shared` has no crypto
 * (it is imported by the browser bundle).
 *
 * A `data:` URI whose media type is not allowed (`image/svg+xml`, `text/html`) is
 * left untouched for the sanitizer to strip — silently dropping it here would hide
 * the reason from the caller, and rewriting it to a `cid:` would smuggle exactly the
 * payload the allow-list exists to reject.
 */
export function inlineImagesAsCid(html, prefix = 'inline-image') {
    const input = String(html ?? '');
    if (!input.includes('data:image/'))
        return { html: input, images: [] };
    const images = [];
    // Matches the src of an <img>; the value is validated by the strict full-string
    // pattern above before anything is rewritten.
    const out = input.replace(/(<img\b[^>]*?\bsrc\s*=\s*)(["'])(data:[^"']+)\2/gi, (whole, head, quote, uri) => {
        const m = INLINE_IMAGE_DATA_URI_RE.exec(uri.trim());
        if (!m)
            return whole;
        const mimeType = m[1].toLowerCase();
        const contentBase64 = m[2].replace(/\s+/g, '');
        const cid = `${prefix}-${images.length + 1}`;
        images.push({
            cid,
            mimeType,
            contentBase64,
            filename: `${cid}.${extensionFor(mimeType)}`,
        });
        return `${head}${quote}cid:${cid}${quote}`;
    });
    return { html: out, images };
}
//# sourceMappingURL=inlineImages.js.map